/**
 * ColumnSelector — shows detected columns for one sheet with check/uncheck.
 */

import React from 'react';
import { ColumnInfo, DETECTION_METHOD_LABELS } from '../types';

interface Props {
  sheetName: string;
  allColumns: string[];
  detectedColumns: ColumnInfo[];
  selected: string[];
  onChange: (selected: string[]) => void;
  /** {column: dialable cell count} for EVERY column, so the ones we did not pre-tick
   *  still say how many numbers they hold instead of leaving the user to guess. */
  dialableCounts?: Record<string, number>;
}

export const ColumnSelector: React.FC<Props> = ({
  sheetName, allColumns, detectedColumns, selected, onChange, dialableCounts,
}) => {
  const detectedNames = new Set(detectedColumns.map((c) => c.name));

  const toggle = (name: string) => {
    onChange(
      selected.includes(name) ? selected.filter((s) => s !== name) : [...selected, name]
    );
  };

  // Show detected columns first, then offer "add more" from all columns
  return (
    <div style={{ marginBottom: 24 }}>
      <h4 style={{ margin: '0 0 8px', color: 'var(--text-soft)' }}>📋 {sheetName}</h4>

      {detectedColumns.length === 0 && (
        <p style={{ color: 'var(--text-faint)', fontSize: 13 }}>No phone columns detected automatically. Select manually below.</p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {detectedColumns.map((col) => (
          <ColumnCard
            key={col.name}
            col={col}
            checked={selected.includes(col.name)}
            onToggle={() => toggle(col.name)}
            highlighted
          />
        ))}

        {/* Offer undetected columns for manual selection */}
        {allColumns.filter((c) => !detectedNames.has(c)).map((colName) => (
          <div
            key={colName}
            onClick={() => toggle(colName)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 12px',
              border: `1px solid ${selected.includes(colName) ? 'var(--amber)' : 'var(--border)'}`,
              borderRadius: 6, cursor: 'pointer',
              background: selected.includes(colName) ? 'var(--surface-subtle)' : 'var(--surface)',
            }}
          >
            <input
              type="checkbox"
              checked={selected.includes(colName)}
              onChange={() => {}}
              style={{ marginRight: 4 }}
            />
            <span style={{ fontSize: 13, color: 'var(--text-soft)', flex: 1, minWidth: 0 }}>
              {colName || <em style={{ color: 'var(--text-faint)' }}>(unnamed column)</em>}
            </span>
            {dialableCounts && (
              <span style={{ fontSize: 12, whiteSpace: 'nowrap',
                             color: (dialableCounts[colName] ?? 0) > 0 ? 'var(--amber)' : 'var(--text-faint)' }}>
                {(dialableCounts[colName] ?? 0) > 0
                  ? `${dialableCounts[colName]} numbers`
                  : 'no numbers'}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

interface CardProps {
  col: ColumnInfo;
  checked: boolean;
  onToggle: () => void;
  highlighted?: boolean;
}

const ColumnCard: React.FC<CardProps> = ({ col, checked, onToggle, highlighted }) => {
  const [showSamples, setShowSamples] = React.useState(false);

  return (
    <div
      style={{
        border: `2px solid ${checked ? 'var(--amber)' : 'var(--border)'}`,
        borderRadius: 8,
        padding: '10px 14px',
        background: checked ? 'var(--surface-subtle)' : 'var(--surface)',
        cursor: 'pointer',
      }}
      onClick={onToggle}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <input type="checkbox" checked={checked} onChange={() => {}} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>
            {col.display_name}
          </span>
          <span style={{
            marginLeft: 8, fontSize: 11, color: 'var(--text-faint)',
            background: 'var(--surface-muted)', padding: '2px 6px', borderRadius: 10,
          }}>
            {DETECTION_METHOD_LABELS[col.detection_method] || col.detection_method}
          </span>
          {col.has_suffixes && (
            <span style={{ marginLeft: 4, fontSize: 11, color: '#f59e0b' }} title="Contains HQ/ext annotations">🏷️</span>
          )}
          {col.has_multi_number && (
            <span style={{ marginLeft: 4, fontSize: 11, color: '#8b5cf6' }} title="Contains cells with multiple numbers">2️⃣</span>
          )}
        </div>
        <span style={{ fontSize: 12, color: 'var(--text-faint)', whiteSpace: 'nowrap' }}>
          ~{col.estimated_count} numbers
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); setShowSamples((s) => !s); }}
          style={{
            background: 'none', border: '1px solid var(--border)', borderRadius: 4,
            padding: '2px 8px', fontSize: 11, cursor: 'pointer', color: 'var(--text-soft)',
          }}
        >
          {showSamples ? 'Hide' : 'Samples'}
        </button>
      </div>
      {showSamples && (
        <div style={{ marginTop: 8, paddingLeft: 28 }}>
          {col.sample_values.slice(0, 5).map((v, i) => (
            <code key={i} style={{
              display: 'block', fontSize: 11, color: 'var(--text-soft)',
              background: 'var(--surface-subtle)', padding: '2px 6px', marginBottom: 2, borderRadius: 4,
            }}>
              {v}
            </code>
          ))}
        </div>
      )}
    </div>
  );
};
